import { API_CALL } from 'actions/actionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import { FETCH_LOYALTY_ACTION_REWARDS } from 'actions/apiActionConstants'
import type { LoyaltyActionRewardCalculateEarnResult } from 'api/mappers/loyalty/mapLoyaltyActionRewards'
import requestLoyaltyCustomerActionRewards from 'api/loyalty/requestLoyaltyCustomerActionRewards'
import { requestLoyaltyActionRewardsEarnCalculationBatch } from 'api/loyalty/requestLoyaltyActionRewardsEarnCalculation'
import { arrayToMap } from 'lib/array/arrayUtils'
import { pickKeys } from 'lib/object/objectUtils'
import { isSupportedLoyaltyActionReward } from 'loyalty/constants/loyaltyActionRewards'
import getLoyaltyActionRewardEarnRequest from 'loyalty/lib/getLoyaltyActionRewardEarnRequest'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'

interface FetchLoyaltyActionRewardsOptions {
  // Re-fetch
  force?: boolean
}

/**
 * Expects external states to be loaded: luxPlus offers, auth account, loyalty.
 */
function fetchLoyaltyActionRewards(
  { force = false }: FetchLoyaltyActionRewardsOptions = {},
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const customerId = state.auth.account.memberId
    const actionRewards = state.loyalty.actionRewards

    if (!getIsLoyaltyEnabled(state) || !customerId) {
      return
    }

    if (!force && (actionRewards.fetching || actionRewards.data)) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LOYALTY_ACTION_REWARDS,
      request: async() => {
        const records = await requestLoyaltyCustomerActionRewards(customerId)

        const candidates = records.filter(isSupportedLoyaltyActionReward)

        // Earn enrichment is best-effort: failures hide earnEstimate, not the catalogue.
        const earnResults: Array<LoyaltyActionRewardCalculateEarnResult> = candidates.length === 0
          ? []
          : await requestLoyaltyActionRewardsEarnCalculationBatch(
              customerId,
              candidates.map((record) => ({
                action: record.actionName,
                // Hidden dependency: resolvers may read other slices (LuxPlus
                // catalog for LuxPlusRenewal). This action does not load them.
                metadata: getLoyaltyActionRewardEarnRequest(
                  record.actionName,
                  getState(),
                ),
              })),
            ).catch(() => [])

        const earnByAction = arrayToMap(
          earnResults.filter((result): result is Extract<LoyaltyActionRewardCalculateEarnResult, { status: 'success' }> =>
            result.status === 'success',
          ),
          (result) => result.action,
          (result) => pickKeys(['bonusPoints', 'statusCredits', 'bonusStatusCredits'], result),
        )

        return candidates.map((reward) => {
          const earnEstimate = earnByAction.get(reward.actionName)
          return earnEstimate ? { ...reward, earnEstimate } : reward
        })
      },
    })
  }
}

export default fetchLoyaltyActionRewards
